/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2019, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import { clone, getRelatedInvertedFilterWidgetSpecs, getRelatedInvertedTopBottomWidgetSpecs } from './ClientSideRecommendationUtils';
import StringResources from 'nls/StringResources';


const itemStaticTitle = (details) => {

	if (!details || !details.type) {
		return;
	}

	let title;
	switch(details.type) {
		case 'PART_TO_WHOLE': {
			const column = details.byColumn || details.column;
			const info = details.info;
			switch(details.info.type) {
				case 'topcount':
					title = StringResources.get('comparison_recommendation_part_to_whole_topcount', {
						column: column.label,
						value: info.value
					});
					break;
				case 'toppercent':
					title = StringResources.get('comparison_recommendation_part_to_whole_top_percentage', {
						column: column.label,
						value: info.value
					});
					break;
				case 'bottomcount':
					title = StringResources.get('comparison_recommendation_part_to_whole_bottomcount', {
						column: column.label,
						value: info.value
					});
					break;
				case 'bottompercent':
					title = StringResources.get('comparison_recommendation_part_to_whole_bottom_percentage', {
						column: column.label,
						value: info.value
					});
					break;
			}
			break;
		}
		case 'REPLACE_COMPARABLE_MEASURE':
			title = StringResources.get('comparison_recommendation_replace_comparable_measure', {
				column: details.to.label
			});
			break;
		case 'INVERT_FILTER_LOCAL':
			title = StringResources.get('comparison_recommendation_invert_local_filter', {
				column: details.column.label
			});
			break;
		case 'INVERT_FILTER_TOP_BOTTOM': {
			const column = details.byColumn /* contextual top/bottom */ || details.column /* non-contextual top/bottom */;
			const label = column.label || column.id;
			const info = details.info;
			switch(details.info.type) {
				case 'topcount':
					title = StringResources.get('comparison_recommendation_invert_top_bottom__topcount', { value: info.value, column: label });
					break;
				case 'toppercent':
					title = StringResources.get('comparison_recommendation_invert_top_bottom__top_percentage', { value: info.value, column: label });
					break;
				case 'bottomcount':
					title = StringResources.get('comparison_recommendation_invert_top_bottom__bottomcount', { value: info.value, column: label });
					break;
				case 'bottompercent':
					title = StringResources.get('comparison_recommendation_invert_top_bottom__bottom_percentage', { value: info.value, column: label });
					break;
			}
			break;
		}
	}
	return title;
};

const SECTIONS = [
	{
		reasons: ['PART_TO_WHOLE', 'REPLACE_COMPARABLE_MEASURE'],
		headerText: function(items) {
			const item = items[0];
			let title;
			switch(item.details.type) {
				case 'PART_TO_WHOLE': {
					let column;
					const byColumn = item.details.byColumn;
					if (byColumn && byColumn.isProjected) {
						column = item.details.byColumn;
					} else {
						column = item.details.column;
					}
					title = column.label;

					break;
				}
				case 'REPLACE_COMPARABLE_MEASURE':
					title = item.details.from.label;
					break;
			}
			return title;
		}
	},
	{
		reasons: ['INVERT_FILTER_LOCAL'],
		headerText: () => {
			return StringResources.get('compareChooser_recommendations_headers_filters');
		}
	},
	{
		reasons: ['INVERT_FILTER_TOP_BOTTOM'],
		headerText: () => {
			return StringResources.get('compareChooser_recommendations_headers_top_bottom');
		}
	}
];

const indexRecommendations = (items, sections) => {

	const results = sections.map(section => {
		return {
			section,
			items: []
		};
	});

	items.forEach(item => {
		if (item.details && item.details.type) {
			const section = results.find(result => result.section.reasons.indexOf(item.details.type) !== -1);
			if (section) {
				section.items.push(item);
			}
		}
	});

	return results;
};

export default class CompareCardSmartsRecommendations {

	/**
	 * Constructor
	 *
	 * @param {Object} options - options
	 * @param {DashboardAPI} options.dashboardApi
	 * @param {Function} options.isFeatureEnabled
	 */
	constructor(options = {}) {
		this.dashboardApi = options.dashboardApi;
		this.isSmartsEnabled = options.isSmartsEnabled;
	}

	isSmartsEnabled() {
		return this.isSmartsEnabled();
	}

	/**
	 * Fetch recommendations
	 *
	 * @return Promise
	 */
	fetchRecommendations(widgetWrapper) {
		if (this.isSmartsEnabled()) {
			return this.fetchRecommendationsFromSmarts(widgetWrapper);
		} else {
			return this.fetchRecommendationsClientSide(widgetWrapper);
		}
	}

	_filterOutUnsupported(visApi, recommendations = []) {
		return recommendations.filter( (recommendation) => {
			const blackList = [
				'summary',
				'kpi',
				'com.ibm.vis.decisiontree',
				'com.ibm.vis.spiral',
				'com.ibm.vis.rave2comet',
				'com.ibm.vis.sunburst'
			];
			const visId = visApi.getVisId();
			if( blackList.includes(visId) && recommendation.details.reason === 'RECOMMEND_FILTER' ) {
				return false;
			}
			return true;
		} );
	}

	fetchRecommendationsFromSmarts(widgetWrapper = {}) {
		const widgetApi = widgetWrapper.widgetApi;
		const widgetModel = widgetWrapper.widgetModel;
		const content = widgetApi && this.dashboardApi.getCanvas().getContent(widgetApi.getId());
		const visualization = content && content.getFeature('Visualization');
		const slots = visualization && visualization.getSlots().getMappedSlotList();

		//query recommendations only if there is data item
		if (!widgetApi || !widgetApi.getVisApi || !visualization || slots.length === 0 || !widgetModel) {
			return Promise.resolve([]);
		}

		const visApi = widgetApi.getVisApi();

		const smartVisRecommender = content.getFeature('Visualization.SmartsRecommender.deprecated');

		if (!smartVisRecommender || !smartVisRecommender.recommendCompareVisualizationsAsWidgetSpecs) {
			return Promise.resolve([]);
		}

		const widgetSpec = widgetModel.toJSON();

		return smartVisRecommender.recommendCompareVisualizationsAsWidgetSpecs({
			widgetSpec,
			visApi
		}).then((recommendations) => {
			const visDefinitions = this.dashboardApi.getFeature('VisDefinitions');
			const filteredRecommendations = this._filterOutUnsupported(visApi, recommendations);

			const items = filteredRecommendations
				.map(({
					spec: recommendedWidgetSpec,
					details
				}) => {
					void(visApi);
					const properties =  [
						{
							id: 'itemAxis.labels.layoutMode',
							value: 'stagger'
						},
						{
							id: 'widget.legend.position',
							value: 'right'
						}
					];
					return {
						staticTitle: itemStaticTitle(details),
						widgets: [
							{
								spec: this._buildSpec(properties, widgetSpec),
								visDefinition: visDefinitions.getById(widgetSpec.visId)
							},
							{
								spec: this._buildSpec(properties, recommendedWidgetSpec),
								visDefinition: visDefinitions.getById(recommendedWidgetSpec.visId),
								isHighlighted: true
							}
						],
						details
					};
				});

			return indexRecommendations(items, SECTIONS);
		});
	}

	fetchRecommendationsClientSide(widgetWrapper = {}) {
		const widgetApi = widgetWrapper.widgetApi;
		const widgetModel = widgetWrapper.widgetModel;
		const visualization = this.dashboardApi.getCanvas().getContent(widgetApi.getId()).getFeature('Visualization');
		const slots = visualization && visualization.getSlots().getMappedSlotList();

		if (!widgetApi.getVisApi || !visualization || slots.length === 0) {
			return Promise.resolve([]);
		}

		const visApi = widgetApi.getVisApi();

		const widgetSpec = widgetModel.toJSON();

		let compareCardRecommendations = getRelatedInvertedFilterWidgetSpecs(widgetSpec, visApi);
		const topBottomRecs = getRelatedInvertedTopBottomWidgetSpecs(widgetSpec, visApi);
		if(topBottomRecs.length > 0) {
			compareCardRecommendations = compareCardRecommendations.concat(topBottomRecs);
		}

		const visDefinitions = this.dashboardApi.getFeature('VisDefinitions');
		return compareCardRecommendations
			.map(({
				spec: recommendedWidgetSpec,
				title
			}) => {
				return {
					widgets: [
						{
							spec: this._buildSpec(null, widgetSpec),
							visDefinition: visDefinitions.getById(widgetSpec.visId)
						},
						{
							spec: this._buildSpec(null, recommendedWidgetSpec),
							visDefinition: visDefinitions.getById(widgetSpec.visId),
							isHighlighted: true
						}
					],
					name: title
				};
			});
	}

	//TODO: Invesigate to migrate to Utils
	_buildSpec(properties,  widgetSpec = {}){
		const widgetSpecCopy = clone(widgetSpec);
		const specPart = properties? { properties , id: undefined } : { id: undefined };
		return Object.assign(widgetSpecCopy, specPart );
	}
}



// WEBPACK FOOTER //
// ./src/js/features/compare/recommendations/CompareCardSmartsRecommendations.js